/* eslint-disable react/button-has-type */

"use client"

import type { ClassValue } from "clsx"
import clsx from "clsx"
import Link from "next/link"
import React from "react"
import type { ButtonHTMLAttributes } from "react"

import ButtonArrowLarge from "../../../public/button-arrow-large.svg"
import ButtonArrow from "../../../public/button-arrow.svg"

interface BaseButtonStyleProps {
  theme?: "dark" | "light" | "blue"
  size?: "lg" | "md" | "sm" | "xs"
  variant?: "primary" | "secondary" | "tertiary" | "disabled"
  type?: "button" | "submit" | "reset"
}

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    BaseButtonStyleProps {
  to?: string | undefined | null
  className?: string
  children: string | React.ReactNode
  animated?: boolean
  includeArrow?: boolean
  target?: string
}

export interface ContentfulButton extends BaseButtonStyleProps {
  _id: string
  ctaLink: string
  ctaText: string
}

const CircleAnimation: React.FC = () => (
  <>
    <div className="circle-cover" />
    <div className="circle-holder">
      <div className="circle circle-12" />
      <div className="circle circle-11" />
      <div className="circle circle-10" />
      <div className="circle circle-9" />
      <div className="circle circle-8" />
      <div className="circle circle-7" />
      <div className="circle circle-6" />
      <div className="circle circle-5" />
      <div className="circle circle-4" />
      <div className="circle circle-3" />
      <div className="circle circle-2" />
      <div className="circle circle-1" />
    </div>
  </>
)

interface ButtonArrowProps {
  size?: string
}
const ButtonArrowForSize: React.FC<ButtonArrowProps> = ({ size }) => {
  if (size === "lg") {
    return <ButtonArrowLarge />
  }
  return <ButtonArrow />
}

const Button: React.FC<ButtonProps> = (props: ButtonProps) => {
  const {
    theme = "dark",
    animated = true,
    size = "md",
    variant = "primary",
    to = "",
    children,
    className,
    target = "",
    type = "button",
    ...buttonProps
  } = props

  let addAnimation = animated

  const classes: ClassValue[] = [className, ""]

  // add base text styles
  if (size === "sm") classes.push("button2")
  else classes.push("button1")

  // padding for primary and secondary. Tertiary has 0 padding.
  if (variant !== "tertiary") {
    // 8px 8px 8px 12px
    if (size === "sm") classes.push("py-2 pl-3 pr-2")

    // 8px 12px 8px 16px
    if (size === "md") classes.push("med py-2 pl-4 pr-3")

    // 12px 12px 12px 20px
    if (size === "lg") classes.push("large py-3 pl-5 pr-3")
  }

  // Blue button with circle animation.
  if (variant === "primary") {
    classes.push("button button-dark rounded-4xl bg-blue-50")
  }

  // secondary buttons. Blue border with light blue bg on hover only.
  if (variant === "secondary") {
    // No animation needed.
    addAnimation = false
    classes.push(
      "button-secondary rounded-4xl border-blue-50 border-solid hover:text-blue-55",
    )

    // Borders are different for each size
    // Adds styles for the small size of buttons
    if (size === "sm") classes.push("border-[1.5px]")

    // Adds styles for the medium size of buttons
    if (size === "md") classes.push("border-[1.75px]")

    // Adds styles for the large size of buttons
    if (size === "lg") classes.push("border-2")

    if (theme === "light") {
      classes.push("text-gray-90 hover:bg-blue-5 hover:border-blue-5")
    } else classes.push("text-gray-2.5 hover:bg-blue-10 hover:border-blue-10")
  }

  // Tertiary. Text only. No background or border.
  // arrow should change blue
  if (variant === "tertiary") {
    classes.push("button-tertiary")

    // 8px 12px 8px 16px
    if (size === "md") classes.push("med")

    // 12px 12px 12px 20px
    if (size === "lg") classes.push("large")

    if (theme === "light") {
      classes.push("text-gray-90 button-arrow-light")
    } else classes.push("text-gray-2.5 button-arrow-dark")
  }

  if (variant === "disabled") {
    classes.push("disabled rounded-4xl pr-4")

    if (theme === "light") {
      classes.push("text-gray-60 bg-gray-10")
    } else classes.push("text-gray-60 bg-gray-80")

    return (
      <button {...buttonProps} className={clsx(classes)} type="button" disabled>
        <div className="wrapper">
          <span className="relative z-[9] inline-block">{children}</span>
        </div>
      </button>
    )
  }

  // Determine if the button should be wrapped with a Link
  const isLink = to && type !== "submit"

  // The button element
  const buttonElement = (
    <button {...buttonProps} className={clsx(classes)} type={type}>
      <div className="wrapper">
        <span className="relative z-[9] inline-block">
          {children}
          <ButtonArrowForSize size={size} />
        </span>
        {addAnimation && variant === "primary" && <CircleAnimation />}
      </div>
    </button>
  )

  if (isLink) {
    return (
      <Link href={to} target={target}>
        {buttonElement}
      </Link>
    )
  }

  return buttonElement
}

export default Button
